<template>
  <v-container>
    <div
      v-if="!checkIfPermission('manage wallet', admin.permissions)"
      class="w-100 h-100 d-flex justify-center align-center"
    >
      <h1 class="error--text">You don't have access to this page</h1>
    </div>
    <div v-else>
      <h1 class="mb-5">Flagged Deposits</h1>
      <user-details-min :userId="minUserId" :open.sync="openMin" />
      <v-row>
        <v-col>
          <v-card flat color="white">
            <v-card-title>
              <v-select
                v-model="selectedStatus"
                :items="status"
                label="Select Status"
                item-text="text"
                item-value="value"
              ></v-select>
              <v-spacer />
              <v-btn
                icon
                class="ml-3"
                :loading="loadingDeposits"
                title="Refresh"
                @click="getDeposits(0)"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="headers"
              :items="arrangedDeposits"
              :loading="loadingDeposits"
              :footer-props="{
                itemsPerPageOptions: [5, 10, 15, 25, 50]
              }"
              @update:items-per-page="updateItemPerPage"
              @update:page="updatePage"
              :server-items-length="apiData.total || 0"
            >
              <template v-slot:no-data>
                <div class="py-6 text-center grey--text">
                  No {{ selectedStatus }} flagged deposits.
                </div>
              </template>
              <template v-slot:[`item.user`]="{ item }">
                <span
                  v-if="item.user_id"
                  class="font-weight-medium cursor-pointer primary--text text-capitalize d-block"
                  @click="openMinDialog(item.user_id)"
                >
                  {{ item.user }}
                </span>
                <span v-else class="grey--text">&mdash;</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-card
                  :color="statusColor(item.status)"
                  flat
                  rounded="md"
                  outlined
                  class="text-capitalize text-center white--text pa-1"
                >
                  {{ item.status }}
                </v-card>
              </template>
              <template v-slot:[`item.date`]="{ item }">
                <small class="font-weight-medium">{{ item.date }}</small>
              </template>
              <template v-slot:[`item.amount`]="{ item }">
                <span>
                  {{ numberWithCommas(item.amount) }}
                  <small class="grey--text text--darken-2">
                    {{ item.currency || 'NGN' }}
                  </small>
                </span>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-btn small depressed @click.stop="openDetails(item)">
                  View Details
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Details + approve/decline -->
      <v-dialog v-model="detailsOpen" max-width="760">
        <v-card v-if="selected" class="pa-4">
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Flagged Deposit - {{ selected.reference }}</span>
            <v-btn icon small @click="detailsOpen = false" aria-label="Close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-progress-linear
              v-if="loadingDetails"
              indeterminate
              class="mb-3"
            ></v-progress-linear>
            <v-alert v-if="selected.reason" type="warning" dense text>
              Held for review: {{ formatReason(selected.reason) }}
            </v-alert>
            <v-row>
              <v-col
                v-for="row in detailRows"
                :key="row.label"
                cols="12"
                md="4"
              >
                <p class="mb-0 font-weight-medium">{{ row.label }}</p>
                <small>{{ row.value }}</small>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <div v-if="selected.status === 'pending'" class="d-flex">
              <v-btn
                text
                color="error"
                :loading="actioning"
                @click.stop="openAction('decline')"
              >
                Decline
              </v-btn>
              <v-btn
                text
                color="success"
                :loading="actioning"
                @click.stop="openAction('approve')"
              >
                Approve
              </v-btn>
            </div>
            <v-btn
              :disabled="actioning"
              text
              color="accent"
              @click="detailsOpen = false"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="actionOpen" max-width="600">
        <v-card>
          <v-card-title class="text-capitalize">
            {{ pendingAction }} deposit
          </v-card-title>
          <v-card-text>
            <p v-if="pendingAction === 'approve'">
              This credits {{ selected && selected.user }}'s wallet with
              {{ selected && numberWithCommas(selected.amount) }}
              {{ (selected && selected.currency) || 'NGN' }} (less the deposit
              fee). Confirm?
            </p>
            <p v-else>
              Nothing is credited and the customer is notified. Confirm?
            </p>
            <v-textarea
              filled
              rows="3"
              label="Remarks (optional)"
              v-model="remarks"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="actioning" @click="actionOpen = false">
              Cancel
            </v-btn>
            <v-btn
              :loading="actioning"
              :color="pendingAction === 'approve' ? 'success' : 'error'"
              class="text-capitalize"
              @click="submitAction"
            >
              Confirm {{ pendingAction }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import walletApi from '../../api/admin/wallet'
import { numberWithCommas } from '../../utils/helpers'
import UserDetailsMin from '../../components/userDetailsMin.vue'

export default {
  components: { UserDetailsMin },
  data: () => ({
    numberWithCommas,
    openMin: false,
    minUserId: '',
    deposits: [],
    apiData: {},
    perPage: 10,
    lastPage: 1,
    loadingDeposits: false,
    loadingDetails: false,
    selected: null,
    detailsOpen: false,
    actionOpen: false,
    actioning: false,
    pendingAction: 'approve',
    remarks: '',
    selectedStatus: 'pending',
    status: [
      { text: 'Pending', value: 'pending' },
      { text: 'Approved', value: 'approved' },
      { text: 'Declined', value: 'declined' }
    ],
    headers: [
      { text: 'Date', align: 'start', value: 'date' },
      { text: 'Username', value: 'user', sortable: false },
      { text: 'Amount', value: 'amount' },
      { text: 'Sender Name', value: 'senderAccountName', sortable: false },
      { text: 'Account Name', value: 'accountHolderName', sortable: false },
      { text: 'Reference', value: 'reference' },
      { text: 'Status', value: 'status' },
      { text: '', value: 'action', sortable: false }
    ]
  }),
  computed: {
    ...mapState({
      admin: (state) => state.admin.admin
    }),
    arrangedDeposits() {
      return this.deposits.map((deposit) => ({
        ...deposit,
        user: deposit.user?.username || '',
        email: deposit.user?.email || '',
        user_id: deposit.user?._id || '',
        date: new Date(deposit.createdAt).toLocaleString()
      }))
    },
    detailRows() {
      const item = this.selected
      if (!item) return []
      const money = (value) =>
        `${numberWithCommas(Number(value || 0))} ${item.currency || 'NGN'}`

      const rows = [
        { label: 'Date', value: item.date },
        { label: 'Status', value: item.status },
        { label: 'User', value: `${item.user} (${item.email})` },
        { label: 'Amount', value: money(item.amount) },
        { label: 'Sender Account Name', value: item.senderAccountName },
        { label: 'Sender Account Number', value: item.senderAccountNumber },
        { label: 'Sender Bank', value: item.senderBankName },
        { label: 'Account Holder Name', value: item.accountHolderName },
        { label: 'Reference', value: item.reference },
        { label: 'Session ID', value: item.sessionId },
        { label: 'Source', value: item.source }
      ]
      if (item.fee != null) rows.push({ label: 'Fee', value: money(item.fee) })
      if (item.netAmount != null) {
        rows.push({ label: 'Credited', value: money(item.netAmount) })
      }
      if (item.actionedBy) {
        rows.push({
          label: 'Actioned By',
          value: item.actionedBy?.username || item.actionedBy
        })
      }
      if (item.actionedAt) {
        rows.push({
          label: 'Actioned At',
          value: new Date(item.actionedAt).toLocaleString()
        })
      }
      if (item.remarks) rows.push({ label: 'Remarks', value: item.remarks })
      return rows.filter((row) => row.value)
    }
  },
  created() {
    this.getDeposits(0)
  },
  watch: {
    selectedStatus() {
      this.lastPage = 1
      this.getDeposits(0)
    }
  },
  methods: {
    statusColor(status) {
      if (status === 'approved') return 'success'
      if (status === 'pending') return 'warning'
      return 'error'
    },
    formatReason(reason) {
      return String(reason).split('_').join(' ')
    },
    openMinDialog(id) {
      this.minUserId = id
      this.openMin = true
    },
    updatePage(page) {
      this.lastPage = page
      this.getDeposits((page - 1) * this.perPage)
    },
    updateItemPerPage(count) {
      this.perPage = count
      this.getDeposits((this.lastPage - 1) * this.perPage)
    },
    alertError(res) {
      this.$store.dispatch('alert', {
        message:
          res.errorMessage?.message ||
          `${res.internalError?.message || 'Something went wrong'}, please try again`,
        error: true,
        status: true
      })
    },
    async getDeposits(offset) {
      this.loadingDeposits = true
      const res = await walletApi.data().getFlaggedDeposits({
        status: this.selectedStatus,
        limit: this.perPage,
        offset
      })
      this.loadingDeposits = false
      if (res.error) return this.alertError(res)

      this.deposits = res.data.data.data || []
      this.apiData = res.data.data.meta || {}
    },
    async openDetails(item) {
      // The list only populates `user`; the detail endpoint also gives us
      // the deciding admin and the virtual account.
      this.selected = item
      this.detailsOpen = true
      this.loadingDetails = true
      const res = await walletApi.data().getFlaggedDeposit(item._id)
      this.loadingDetails = false
      if (res.error) return

      const deposit = res.data.data
      this.selected = {
        ...deposit,
        user: deposit.user?.username || '',
        email: deposit.user?.email || '',
        user_id: deposit.user?._id || '',
        date: new Date(deposit.createdAt).toLocaleString()
      }
    },
    openAction(action) {
      this.pendingAction = action
      this.remarks = ''
      this.actionOpen = true
    },
    async submitAction() {
      this.actioning = true
      const res = await walletApi
        .data()
        .actionFlaggedDeposit(this.selected._id, this.pendingAction, {
          ...(this.remarks ? { remarks: this.remarks } : {})
        })
      this.actioning = false
      if (res.error) return this.alertError(res)

      this.$store.dispatch('alert', {
        message: `Deposit ${this.pendingAction}d`,
        status: true
      })
      this.actionOpen = false
      this.detailsOpen = false
      this.getDeposits((this.lastPage - 1) * this.perPage)
    }
  }
}
</script>

<style></style>
